// src/app/modules/Billing/SubscriptionBlockedPage.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, shallowEqual } from 'react-redux';
import { CreditCard, XCircle, AlertTriangle, ArrowRight, LogOut, Mail } from 'lucide-react';
import './subscription-blocked.scss';

const SubscriptionBlockedPage = () => {
  const navigate = useNavigate();

  const { organization } = useSelector(
    ({ auth }) => ({ organization: auth.organization }),
    shallowEqual
  );

  const status      = organization?.subscription?.status;
  const isCancelled = status === 'cancelled';
  const plan        = organization?.subscription?.plan || 'unknown plan';
  const orgName     = organization?.name || 'Your organization';
  const orgInitials = orgName.slice(0, 2).toUpperCase();

  const steps = isCancelled
    ? [
        { n: '01', text: 'Choose a plan that fits your team' },
        { n: '02', text: 'Add or confirm your payment method' },
        { n: '03', text: 'Access restored immediately' },
      ]
    : [
        { n: '01', text: 'Update your payment method below' },
        { n: '02', text: 'We\'ll retry the failed charge' },
        { n: '03', text: 'Access restored immediately' },
      ];

  return (
    <div className="sb-page">

      {/* ── Left panel ── */}
      <div className="sb-left">

        {/* Org chip */}
        <div className="sb-org-chip">
          <div className="sb-org-chip__avatar">{orgInitials}</div>
          <div className="sb-org-chip__info">
            <span className="sb-org-chip__name">{orgName}</span>
            <span className="sb-org-chip__plan">{plan}</span>
          </div>
          <div className={`sb-org-chip__badge sb-org-chip__badge--${status}`}>
            {status}
          </div>
        </div>

        {/* Status banner */}
        <div className={`sb-status-banner sb-status-banner--${isCancelled ? 'cancelled' : 'suspended'}`}>
          <div className="sb-status-banner__icon">
            {isCancelled
              ? <XCircle size={18} />
              : <AlertTriangle size={18} />
            }
          </div>
          <div>
            <div className="sb-status-banner__title">
              {isCancelled ? 'Subscription cancelled' : 'Payment failed'}
            </div>
            <div className="sb-status-banner__sub">
              {isCancelled
                ? 'Your plan was cancelled and workspace access has been paused.'
                : 'We couldn\'t process your last payment. Update your details to reactivate.'}
            </div>
          </div>
        </div>

        {/* Steps */}
        <div className="sb-steps">
          <div className="sb-steps__label">How to restore access</div>
          {steps.map((s) => (
            <div key={s.n} className="sb-step">
              <div className="sb-step__num">{s.n}</div>
              <div className="sb-step__text">{s.text}</div>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="sb-actions">
          <button
            className="sb-btn sb-btn--primary"
            onClick={() => navigate('/settings?tab=billing')}
          >
            <CreditCard size={15} />
            {isCancelled ? 'Resubscribe' : 'Update payment method'}
            <ArrowRight size={14} className="sb-btn__arrow" />
          </button>
          <button
            className="sb-btn sb-btn--ghost"
            onClick={() => navigate('/logout')}
          >
            <LogOut size={14} />
            Sign out
          </button>
        </div>

        <div className="sb-support">
          <Mail size={12} />
          Need help? <a href="mailto:info@sety.io">Contact support</a>
        </div>
      </div>

      {/* ── Right panel — illustrative ── */}
      <div className="sb-right">
        <div className="sb-right__inner">

          {/* Muted "locked" dashboard preview */}
          <div className="sb-preview">
            <div className="sb-preview__label">Your workspace</div>

            {/* Fake KPI row */}
            <div className="sb-fake-kpis">
              {[
                { label: 'Security Score', w: '52%' },
                { label: 'Currently Inside', w: '38%' },
                { label: 'Active Threats', w: '60%' },
                { label: "Today's Activity", w: '45%' },
              ].map((k) => (
                <div key={k.label} className="sb-fake-kpi">
                  <div className="sb-fake-kpi__icon sb-blur" />
                  <div className="sb-fake-kpi__body">
                    <div className="sb-fake-kpi__lbl">{k.label}</div>
                    <div className="sb-fake-kpi__val sb-blur" style={{ width: k.w }} />
                    <div className="sb-fake-kpi__sub sb-blur" style={{ width: '70%' }} />
                  </div>
                </div>
              ))}
            </div>

            {/* Fake chart */}
            <div className="sb-fake-chart">
              <div className="sb-fake-chart__header">
                <div className="sb-blur" style={{ height: 10, width: 120, borderRadius: 4 }} />
              </div>
              <div className="sb-fake-chart__bars">
                {[55, 72, 48, 88, 61, 79, 42].map((h, i) => (
                  <div key={i} className="sb-fake-bar sb-blur" style={{ height: `${h}%` }} />
                ))}
              </div>
            </div>

            {/* Lock overlay */}
            <div className="sb-preview__lock">
              <div className="sb-lock-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect width="18" height="11" x="3" y="11" rx="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              </div>
              <p>Resource locked</p>
              <span>Restore access to continue</span>
            </div>
          </div>

        </div>
      </div>

    </div>
  );
};

export default SubscriptionBlockedPage;